import React from 'react';
import type { CompatReportDocument } from '../types';
import { bandWords, formatPct, nineInTen } from '../helpers/formatReport';
import { DistributionChart } from './DistributionChart';
import { ArrivalOrderPanel, type ArrivalOrderState } from './ArrivalOrderPanel';

interface OverviewViewProps {
  result: CompatReportDocument;
  /** Job/report id from submit ticket — required for sequence replay. */
  reportId?: string | null;
  arrivalOrder: ArrivalOrderState;
  onArrivalOrderChange: (next: ArrivalOrderState) => void;
}

export const OverviewView: React.FC<OverviewViewProps> = ({
  result,
  reportId = null,
  arrivalOrder,
  onArrivalOrderChange,
}) => {
  const w = result.weldability;
  const deadEnds = w.deadEnds ?? null;
  // Lead with usable pool when line-enders exist (sample-ui / PDF).
  const src = deadEnds?.withoutThem ?? w;
  const deadNames = deadEnds ? deadEnds.pipes.map((p) => p.pipeId).join(', ') : '';
  const placed = Math.round(src.pipesPlacedAvg);
  const confPct = (result.run.confidence * 100).toFixed(0);
  const orders = result.run.orders ?? 0;
  const maxRunIndex = Math.max(0, orders - 1);
  const fitRate =
    src.pipesTotal > 0 ? src.pipesPlacedAvg / src.pipesTotal : w.fitRate;
  const commit = nineInTen(src);
  const band = bandWords(src);

  return (
    <div className="space-y-4 max-w-full mx-auto w-full">
      {deadEnds?.statement ? (
        <div className="rounded-xl border border-readable-red bg-white p-5 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-readable-red" />
          <p className="text-[11px] font-semibold tracking-wider uppercase text-readable-red mb-3">
            Read this first
          </p>
          <div className="border-l-[3px] border-readable-red bg-[rgba(196,0,31,0.05)] rounded-r-lg px-4 py-3 text-[13.5px] text-text-grey">
            <span className="font-semibold text-blueprint-blue">{deadEnds.statement}</span>
          </div>
        </div>
      ) : null}

      <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-galvanizing-green" />
        <div className="font-heading font-bold text-blueprint-blue text-[clamp(2.5rem,8vw,4rem)] leading-none">
          {placed}
          <span className="text-[20px] text-heritage-grey font-semibold ml-1.5">
            of {src.pipesTotal} pipes
          </span>
        </div>
        <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey mt-2">
          weld into one line
        </p>
        {deadEnds ? (
          <p className="mt-1 text-[14px] font-semibold text-readable-red">
            after setting aside {deadNames} (details in the red box above)
          </p>
        ) : null}
        <p className="mt-3.5 text-[15px] text-text-grey">
          The exact average across builds is{' '}
          <span className="font-semibold text-blueprint-blue">
            {src.pipesPlacedAvg.toFixed(1)} of {src.pipesTotal}
          </span>
          , which is <span className="font-semibold text-blueprint-blue">{formatPct(fitRate)}</span>
          . We are <span className="font-semibold text-blueprint-blue">{confPct}%</span> sure the
          true average sits{' '}
          <span className="font-semibold text-blueprint-blue">{band}</span> pipes.
          {commit != null ? (
            <>
              {' '}
              <span className="font-semibold text-blueprint-blue">
                In 9 builds out of 10, at least {commit} pipes weld in.
              </span>
            </>
          ) : null}{' '}
          A pipe that does not fit is set aside and tried again after the next weld, which is normal
          practice.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
          {[
            { v: src.worstOrder, k: 'Toughest build' },
            { v: src.typicalOrder, k: 'Typical build' },
            { v: src.bestOrder, k: 'Best build' },
          ].map((s) => (
            <div
              key={s.k}
              className="bg-page-bg border border-[#E2E5EA] rounded-[10px] p-3 text-center"
            >
              <div className="font-heading font-bold text-[22px] text-blueprint-blue">{s.v}</div>
              <div className="text-[10px] font-semibold tracking-wide uppercase text-heritage-grey mt-1">
                {s.k}
              </div>
            </div>
          ))}
        </div>
      </div>

      {deadEnds?.withoutThem ? (
        <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#E6A100]" />
          <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey mb-3">
            What you asked vs what you get
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-page-bg border border-[#E2E5EA] rounded-[10px] p-3 text-center">
              <div className="font-heading font-bold text-[22px] text-blueprint-blue">
                {Math.round(w.pipesPlacedAvg)} of {w.pipesTotal}
              </div>
              <div className="text-[10px] font-semibold tracking-wide uppercase text-heritage-grey mt-1">
                As scanned
              </div>
              <div className="text-[10px] text-heritage-grey mt-1">every pipe in the batch</div>
            </div>
            <div className="bg-page-bg border border-[#E2E5EA] rounded-[10px] p-3 text-center">
              <div className="font-heading font-bold text-[22px] text-blueprint-blue">
                {Math.round(deadEnds.withoutThem.pipesPlacedAvg)} of{' '}
                {deadEnds.withoutThem.pipesTotal}
              </div>
              <div className="text-[10px] font-semibold tracking-wide uppercase text-heritage-grey mt-1">
                Usable pool
              </div>
              <div className="text-[10px] text-heritage-grey mt-1">flagged pipes set aside</div>
            </div>
          </div>
          <p className="mt-3 text-[13px] text-heritage-grey">
            The as-scanned number is what this exact batch does; the usable pool is what the pipes
            are worth.
          </p>
        </div>
      ) : null}

      {deadEnds?.withoutThem?.distribution?.length ? (
        <>
          <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-galvanizing-green" />
            <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey mb-3">
              Spread across {orders} random orders, {deadNames} set aside (the usable pool)
            </p>
            <DistributionChart
              distribution={deadEnds.withoutThem.distribution}
              color="#1F9A3A"
              unitLabel="pipes welded into the line, per build"
            />
          </div>
          <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#E6A100]" />
            <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey mb-3">
              Same {orders} orders, as scanned ({deadNames} included)
            </p>
            <DistributionChart
              distribution={w.distribution}
              color="#E6A100"
              unitLabel="pipes welded into the line, per build"
            />
          </div>
        </>
      ) : (
        <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-hero-red" />
          <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey mb-3">
            Spread across {orders} random orders
          </p>
          <DistributionChart
            distribution={w.distribution}
            color="#1F9A3A"
            unitLabel="pipes welded into the line"
          />
        </div>
      )}

      {result.run.stopStatement && (
        <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-hero-red" />
          <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey mb-3">
            Why {orders} builds?
          </p>
          <div className="border-l-[3px] border-blueprint-blue bg-[rgba(0,39,63,0.05)] rounded-r-lg px-4 py-3 text-[13.5px] text-text-grey">
            <span className="font-semibold text-blueprint-blue">{result.run.stopStatement}</span>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-hero-red" />
        <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey mb-4">
          How to read this report (the usual questions, PDF&apos;s last page)
        </p>
        <div className="space-y-4 text-[15px] text-text-grey">
          <p>
            <span className="font-semibold text-blueprint-blue">
              Why is the answer a range and not one number?
            </span>{' '}
            The arrival order changes the outcome. Across {orders.toLocaleString()} builds the best
            welded {src.bestOrder}, a typical one {src.typicalOrder}, the toughest {src.worstOrder}.
            One number would hide that.
          </p>
          <p>
            <span className="font-semibold text-blueprint-blue">
              What does &quot;{confPct}% sure, {band}&quot; mean?
            </span>{' '}
            It is about the average, not any single build. Repeat the whole exercise 100 times and
            the average lands there about {confPct} of them; single builds swing wider.
          </p>
          <p>
            <span className="font-semibold text-blueprint-blue">What does Watch mean on a pipe?</span>{' '}
            It welds in fewer than 9 builds out of 10 (the line is at 90.0%; the decimal decides:
            89.9% reads Watch, 90.2% reads Safe). Watch is not a defect; keep the pipe where a second
            try is cheap.
          </p>
          <p>
            <span className="font-semibold text-blueprint-blue">
              Why do numbers change when pipes are added or removed?
            </span>{' '}
            Pipes weld against each other, so removing pipes removes partners for the ones that
            stay. That is why a shortlist is always verified with its own run.
          </p>
          <p>
            <span className="font-semibold text-blueprint-blue">
              Can I check any of these numbers?
            </span>{' '}
            Yes. Every build replays exactly from seed{' '}
            <span className="font-semibold text-blueprint-blue">
              {result.run.baseSeed ?? '—'}
            </span>
            , so any figure can be reproduced later, build by build.
          </p>
        </div>
      </div>

      {orders > 0 && (
        <ArrivalOrderPanel
          reportId={reportId}
          maxRunIndex={maxRunIndex}
          state={arrivalOrder}
          onStateChange={onArrivalOrderChange}
        />
      )}
    </div>
  );
};
